import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import ConsentCheckboxes from "@/components/auth/ConsentCheckboxes";
import { useAuthStore } from "@/stores/authStore";
import { api } from "@/services/api";
import { adminService } from "@/services/adminService";
import { useToast } from "@/hooks/use-toast";

export default function ConsentPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user, clearConsentRequired, fetchCurrentUser, getDefaultLandingPath } = useAuthStore();

  // API에서 현재 버전 약관/개인정보 내용 로드
  const { data: consentDocs, isLoading: isLoadingDocs } = useQuery({
    queryKey: ["consent-documents", "current"],
    queryFn: () => adminService.getCurrentConsentDocuments(),
  });

  const termsDoc = consentDocs?.find((d) => d.document_type === "terms_of_service");
  const privacyDoc = consentDocs?.find((d) => d.document_type === "privacy_policy");

  const [termsAgreed, setTermsAgreed] = useState(false);
  const [privacyAgreed, setPrivacyAgreed] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<{ terms?: string; privacy?: string }>({});

  // 최초 동의 vs 약관 갱신 구분
  const isUpdate = !!user?.consent_version;

  const handleSubmit = async () => {
    const newErrors: { terms?: string; privacy?: string } = {};
    if (!termsAgreed) newErrors.terms = "이용약관에 동의해주세요.";
    if (!privacyAgreed) newErrors.privacy = "개인정보 수집 및 이용에 동의해주세요.";

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsSubmitting(true);
    try {
      await api.post("/auth/accept-consent");
      clearConsentRequired();
      await fetchCurrentUser();
      toast({ title: "동의가 완료되었습니다." });
      navigate(getDefaultLandingPath(), { replace: true });
    } catch {
      toast({
        title: "오류",
        description: "동의 처리 중 오류가 발생했습니다. 다시 시도해주세요.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <Card className="w-full max-w-xl">
        <CardHeader className="text-center space-y-3">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
            <ShieldCheck className="h-6 w-6 text-primary" />
          </div>
          <CardTitle className="text-xl">
            {isUpdate ? "약관 변경 안내" : "서비스 이용 약관 동의"}
          </CardTitle>
          <CardDescription>
            {user?.name}님, {isUpdate
              ? "서비스 약관이 변경되었습니다. 변경된 내용을 확인하시고 다시 동의해주세요."
              : "서비스 이용을 위해 아래 약관에 동의해주세요."}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* 안내 */}
          <div className="rounded-lg bg-muted/50 p-4 text-sm text-muted-foreground space-y-2.5">
            <p>
              「개인정보 보호법」 제15조(수집·이용 동의) 및 제22조(동의를 받는 방법)에 따라,
              서비스 이용에 필요한 이용약관과 개인정보 수집·이용에 대한 동의를 받고 있습니다.
            </p>
            <p className="text-xs">
              수집된 개인정보는 <span className="font-medium text-foreground">회원 탈퇴 시까지</span> 보유하며,
              관련 법령에 따라 일부 기록은 최대 5년까지 보존될 수 있습니다.
            </p>
            <p className="text-xs">
              귀하는 언제든지 개인정보의 열람·정정·삭제·처리정지를 요청할 수 있으며,
              동의를 거부할 권리가 있습니다. 다만, 필수 항목에 대한 동의를 거부하실 경우 서비스 이용이 제한됩니다.
            </p>
          </div>

          {/* 동의 체크박스 */}
          {isLoadingDocs ? (
            <div className="space-y-3">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-8 w-full" />
              <Skeleton className="h-8 w-full" />
            </div>
          ) : (
            <ConsentCheckboxes
              termsAgreed={termsAgreed}
              privacyAgreed={privacyAgreed}
              onTermsChange={(checked) => {
                setTermsAgreed(checked);
                if (checked) setErrors((prev) => ({ ...prev, terms: undefined }));
              }}
              onPrivacyChange={(checked) => {
                setPrivacyAgreed(checked);
                if (checked) setErrors((prev) => ({ ...prev, privacy: undefined }));
              }}
              termsContent={termsDoc?.content}
              privacyContent={privacyDoc?.content}
              errors={errors}
            />
          )}

          <Button
            className="w-full"
            onClick={handleSubmit}
            disabled={!termsAgreed || !privacyAgreed || isSubmitting}
          >
            {isSubmitting ? "처리 중..." : "동의하고 계속하기"}
          </Button>

          <p className="text-center text-xs text-muted-foreground">
            동의 내역은 법적 요건에 따라 안전하게 기록·보관됩니다.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
